"use client";

/**
 * useActiveLocation — resolves the currently active ship-to location
 * for the buyer by walking the chain:
 *
 *   1. cart.companyLocationId  (explicit per-cart selection, highest priority)
 *   2. The location with isDefault === true in the allowed-locations list
 *      (customer's default_location_id, computed by the API JOIN)
 *   3. undefined (no location set / anonymous traffic)
 *
 * The hook reads cart state from CartProvider context (the storefront's
 * established cart management pattern) and fetches allowed locations on the
 * client via clientApi — mirroring how LocationsList.tsx consumes the same
 * endpoint for mutations.
 *
 * `resolveActiveLocation` is exported as a pure function so it can be unit-
 * tested without a DOM environment.
 */

import { useState, useEffect } from "react";
import type { AllowedLocation, CartDTO } from "@wmart/shared-types";
import { clientApi } from "../../lib/api-client";
import { useCart } from "@/app/[locale]/(shop)/cart/CartProvider";

// ---------------------------------------------------------------------------
// Pure resolution logic (testable without DOM)
// ---------------------------------------------------------------------------

/**
 * Resolves the active location from the cart + allowed-locations list.
 *
 * @param cart   Current CartDTO, or null when unavailable / anonymous.
 * @param locs   The buyer's allowed locations (from GET /me/allowed-locations).
 * @returns      The matched AllowedLocation, or undefined.
 */
export function resolveActiveLocation(
  cart: CartDTO | null | undefined,
  locs: AllowedLocation[],
): AllowedLocation | undefined {
  if (locs.length === 0) return undefined;

  // Step 1 — explicit cart selection
  const cartLocId = cart?.companyLocationId ?? null;
  if (cartLocId) {
    const cartLoc = locs.find((l) => l.id === cartLocId);
    if (cartLoc) return cartLoc;
  }

  // Step 2 — customer's default location
  return locs.find((l) => l.isDefault);
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * Returns the active AllowedLocation for the current buyer, or undefined
 * when no location is determined (anonymous traffic, no default set, etc.).
 *
 * Must be rendered inside <CartProvider>.
 */
export function useActiveLocation(): AllowedLocation | undefined {
  const { cart } = useCart();
  const [locations, setLocations] = useState<AllowedLocation[]>([]);

  useEffect(() => {
    let cancelled = false;
    clientApi
      .get<AllowedLocation[]>("/v1/storefront/me/allowed-locations")
      .then((data) => {
        if (!cancelled) setLocations(data);
      })
      .catch(() => {
        // 401/403 = anonymous or unapproved buyer — locations stay empty,
        // resolveActiveLocation returns undefined.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return resolveActiveLocation(cart, locations);
}
